import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  Button,
  Breadcrumb,
  Col,
  Form
} from "react-bootstrap";
import {
  Page,
  Spinner,
  Divider,
  ErrorMessages,
  IconButton,
  IconButtonType,
  NoData,
  DeleteConfirmation
} from "@whitespace/whitespace-ui";
import { DateRangePickerComponent } from "@syncfusion/ej2-react-calendars";
import { useCallback, useEffect, useState } from "react";
import { useStateMachine } from "little-state-machine";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import nonWorkingPeriodSchema from "modules/schemas/nonWorkingPeriodSchema";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import {
  makeDateRangePickerAACompliant,
  makeDropDownTreeAACompliant
} from "modules/SyncfusionAAFixer";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import { format } from "date-fns";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IDisposalSite from "interfaces/IDisposalSite";
import IGetSeveritiesResponseModel from "interfaces/Response/IGetSeveritiesResponseModel";
import IUpdateNonWorkingPeriodRequestModel from "interfaces/Request/IUpdateNonWorkingPeriodRequestModel";
import "styles/sf-base.scss";
import "styles/sf-calendar.scss";
import "styles/sf-dropdown.scss";
import {
  convertStringToDate,
  areEqual,
  convertToDateArray
} from "helpers/DateUtils";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const UpdateNonWorkingPeriod = () => {
  const { state } = useStateMachine();
  const [isSending, setIsSending] = useState<boolean>(true);

  const [nonWorkingPeriod, setNonWorkingPeriod] = useState<
  IUpdateNonWorkingPeriodRequestModel | undefined
  >(state.editNonWorkingPeriodRequest);

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IUpdateNonWorkingPeriodRequestModel>({
    resolver: yupResolver(nonWorkingPeriodSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const watchFields = watch(["severity", "active", "name", "description"]);

  const {
    isSending: getSeveritiesIsSending,
    errors: getSeveritiesErrors,
    data: getSeveritiesData,
    sendRequest: getSeveritiesSendRequest
  } = useAmplifyREST();

  const {
    isSending: getDisposalSitesIsSending,
    errors: getDisposalSitesErrors,
    data: getDisposalSitesData,
    sendRequest: getDisposalSitesSendRequest
  } = useAmplifyREST();

  const {
    isSending: updateNonWorkingPeriodIsSending,
    errors: updateNonWorkingPeriodErrors,
    statusCode: updateNonWorkingPeriodStatusCode,
    sendRequest: updateNonWorkingPeriodSendRequest
  } = useAmplifyREST();

  const {
    isSending: deleteNonWorkingPeriodIsSending,
    errors: deleteNonWorkingPeriodErrors,
    statusCode: deleteNonWorkingPeriodStatusCode,
    sendRequest: deleteNonWorkingPeriodSendRequest
  } = useAmplifyREST();

  const [aaFixDateRangePickerCompleted, setAAFixDateRangePickerCompleted] = useState<boolean>(false);
  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);
  const apiErrors = getSeveritiesErrors
    || getDisposalSitesErrors
    || updateNonWorkingPeriodErrors
    || deleteNonWorkingPeriodErrors;

  const navigate = useNavigate();

  let dateRangePickerComponent: DateRangePickerComponent | undefined;
  const [dropDownTreeComponent, setDropDownTreeComponent] = useState<DropDownTreeComponent>();

  const [selectedDateFrom, setSelectedDateFrom] = useState<Date | undefined>();
  const [selectedDateTo, setSelectedDateTo] = useState<Date | undefined>();
  const [selectedDisposalSites, setSelectedDisposalSites] = useState<
  string[] | undefined
  >(nonWorkingPeriod?.disposalSites);

  const [updateButtonDisabled, setUpdateButtonDisabled] = useState(true);
  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [disposalSites, setDisposalSites] = useState<
  IDisposalSite[] | undefined
  >();

  const disposalSiteFields: Object = {
    dataSource: disposalSites,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const disposalSiteTreeSettings = { autoCheck: false };

  const canFormBeSubmitted = () => {
    const name = getValues("name");
    const description = getValues("description");
    const severity = getValues("severity");
    const active = getValues("active");
    let atLeastOneFieldChangedValue = false;

    const requiredFieldsEntered = name.length > 0
      && selectedDateFrom
      && selectedDateTo
      && description.length > 0
      && severity !== ""
      && selectedDisposalSites
      && selectedDisposalSites.length > 0;

    if (requiredFieldsEntered) {
      const disposalSitesRemoved = nonWorkingPeriod?.disposalSites?.filter(
        (x: any) => !selectedDisposalSites?.includes(x)
      ) ?? [];
      const disposalSitesAdded = selectedDisposalSites?.filter(
        (x: any) => !nonWorkingPeriod?.disposalSites?.includes(x)
      ) ?? [];

      atLeastOneFieldChangedValue = name !== nonWorkingPeriod?.name
        || areEqual(selectedDateFrom, nonWorkingPeriod?.dateFrom)
        || areEqual(selectedDateTo, nonWorkingPeriod?.dateTo)
        || description !== nonWorkingPeriod?.description
        || severity !== nonWorkingPeriod?.severity
        || disposalSitesRemoved?.length > 0
        || disposalSitesAdded?.length > 0
        || active !== nonWorkingPeriod.active;
    }

    setUpdateButtonDisabled(
      !(requiredFieldsEntered && atLeastOneFieldChangedValue)
    );
  };

  useEffect(() => {
    setIsSending(
      getSeveritiesIsSending
        || getDisposalSitesIsSending
        || updateNonWorkingPeriodIsSending
        || deleteNonWorkingPeriodIsSending
        || !aaFixDateRangePickerCompleted
        || !aaFixDropDownTreeCompleted
    );
  }, [
    setIsSending,
    getSeveritiesIsSending,
    getDisposalSitesIsSending,
    updateNonWorkingPeriodIsSending,
    deleteNonWorkingPeriodIsSending,
    aaFixDateRangePickerCompleted,
    aaFixDropDownTreeCompleted
  ]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields, canFormBeSubmitted]);

  const datePickerOnChange = (props: any) => {
    // eslint-disable-next-line react/prop-types
    setSelectedDateFrom(props.startDate ?? undefined);
    // eslint-disable-next-line react/prop-types
    setSelectedDateTo(props.endDate ?? undefined);
  };

  const dropDownTreeComponentOnChange = () => {
    setSelectedDisposalSites(dropDownTreeComponent?.value);
  };

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    setNonWorkingPeriod(state.editNonWorkingPeriodRequest);
  }, [state.editNonWorkingPeriodRequest]);

  useEffect(() => {
    setSelectedDateFrom(
      convertStringToDate(state.editNonWorkingPeriodRequest?.dateFrom!)
    );
    setSelectedDateTo(
      convertStringToDate(state.editNonWorkingPeriodRequest?.dateTo!)
    );
  }, [nonWorkingPeriod, setSelectedDateFrom, setSelectedDateTo]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [selectedDateFrom, selectedDateTo, selectedDisposalSites]);

  useEffect(() => {
    getSeveritiesSendRequest("settings/nonworkingperiods/severities", "GET");
  }, [getSeveritiesSendRequest]);

  useEffect(() => {
    getDisposalSitesSendRequest("settings/disposalsites", "GET");
  }, [getDisposalSitesSendRequest]);

  useEffect(() => {
    if (getDisposalSitesData) {
      setDisposalSites(getDisposalSitesData.disposalSites);
    }
  }, [getDisposalSitesData]);

  const handleDeleteNonWorkingPeriod = () => {
    deleteNonWorkingPeriodSendRequest(
      `settings/nonworkingperiods/${nonWorkingPeriod?.id}`,
      "DELETE"
    );
  };

  useEffect(() => {
    if (deleteNonWorkingPeriodStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsNonWorkingPeriods);
    }
  }, [deleteNonWorkingPeriodStatusCode]);

  const handleUpdateNonWorkingPeriodClick = (
    request: IUpdateNonWorkingPeriodRequestModel
  ) => {
    request.dateFrom = format(selectedDateFrom!, "yyyy-MM-dd");
    request.dateTo = format(selectedDateTo!, "yyyy-MM-dd");
    request.disposalSites = selectedDisposalSites!;

    updateNonWorkingPeriodSendRequest(
      `settings/nonworkingperiods/${nonWorkingPeriod?.id}`,
      "PUT",
      request
    );
  };

  useEffect(() => {
    if (updateNonWorkingPeriodStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsNonWorkingPeriods);
    }
  }, [updateNonWorkingPeriodStatusCode]);

  useEffect(() => {
    if (dateRangePickerComponent) {
      makeDateRangePickerAACompliant(true);
      setAAFixDateRangePickerCompleted(true);
    }
  }, [dateRangePickerComponent]);

  useEffect(() => {
    if (dropDownTreeComponent) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [dropDownTreeComponent]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
      setAAFixDateRangePickerCompleted(true);
    }
  }, [apiErrors]);

  const renderSeverities = () => {
    const regions = [
      <option key="" value="">
        Please Select
      </option>
    ];
    if (getSeveritiesData) {
      (getSeveritiesData as IGetSeveritiesResponseModel).severities.forEach(
        severity => {
          regions.push(
            <option key={severity} value={severity}>
              {severity}
            </option>
          );
        }
      );
    }

    return regions;
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}

      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-non-working-period">
              Edit Non-Working Period
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleUpdateNonWorkingPeriodClick)}
        >
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-nonworkingperiods"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsNonWorkingPeriods }}
                    onClick={() => navigate(RoutePathsEnum.SettingsNonWorkingPeriods)}
                  >
                    Non-Working Periods
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    active
                    data-testid="breadcrumb-edit-nonworkingperiod"
                  >
                    {`Edit ${nonWorkingPeriod?.name}`}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  disabled={nonWorkingPeriod?.inUse || isSending}
                  aria-disabled={nonWorkingPeriod?.inUse || isSending}
                  aria-label="Delete Non Working Period"
                  type="button"
                  spin={deleteNonWorkingPeriodIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={
                    updateButtonDisabled || updateNonWorkingPeriodIsSending
                  }
                  aria-disabled={
                    updateButtonDisabled || updateNonWorkingPeriodIsSending
                  }
                  aria-label="Update non-working period"
                  type="submit"
                  spin={isSending}
                  data-testid="update"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsNonWorkingPeriods)}
                  data-testid="cancel"
                  aria-label="Cancel update and back to non-working periods"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="pt-2" data-testid="formElements">
              <Container fluid>
                <Row className="d-flex mt-1 ms-1">
                  <Col xs={12}>
                    <div className="control-pane">
                      <div className="control-section" />
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="name">
                          <Form.Label
                            id="nameLabel"
                            className="horiz-form-label"
                          >
                            Name:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              type="text"
                              role="textbox"
                              defaultValue={nonWorkingPeriod?.name}
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "name",
                                errors,
                                null
                              )}
                              aria-labelledby="nameLabel"
                              maxLength={50}
                              {...register("name")}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="name"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="code">
                          <Form.Label
                            id="dateRangeLabel"
                            className="horiz-form-label"
                          >
                            Date Range:
                          </Form.Label>
                          <Col>
                            <DateRangePickerComponent
                              change={datePickerOnChange}
                              ref={(d: DateRangePickerComponent) => {
                                dateRangePickerComponent = d;
                              }}
                              strictMode
                              id="datepicker"
                              format="dd/MM/yyyy"
                              value={convertToDateArray(
                                selectedDateFrom,
                                selectedDateTo
                              )}
                              htmlAttributes={{
                                "aria-labelledby": "dateRangeLabel",
                                "aria-required": "true"
                              }}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="dateRange"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="description">
                          <Form.Label
                            id="descriptionLabel"
                            className="horiz-form-label"
                          >
                            Description:
                          </Form.Label>
                          <Col>
                            <Form.Control
                              {...register("description")}
                              type="text"
                              role="textbox"
                              defaultValue={nonWorkingPeriod?.description}
                              aria-required="true"
                              aria-invalid={ariaInvalidCheck(
                                "name",
                                errors,
                                null
                              )}
                              aria-labelledby="descriptionLabel"
                              maxLength={50}
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="description"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="severity">
                          <Form.Label
                            id="severityLabel"
                            className="horiz-form-label"
                          >
                            Severity:
                          </Form.Label>
                          <Col>
                            {getSeveritiesData && (
                              <Form.Select
                                {...register("severity", {
                                  onChange: () => canFormBeSubmitted()
                                })}
                                defaultValue={nonWorkingPeriod?.severity}
                                data-testid="ddlSeverity"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="severityLabel"
                              >
                                {renderSeverities()}
                              </Form.Select>
                            )}
                            <NoData
                              visible={
                                !apiErrors
                                && !getSeveritiesIsSending
                                && !getSeveritiesData
                              }
                              description="No Severities Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="severity"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                      <Row className="mb-2">
                        <Form.Group as={Row} controlId="disposalSites">
                          <Form.Label
                            id="disposalSitesLabel"
                            className="horiz-form-label"
                          >
                            Disposal Sites:
                          </Form.Label>
                          <Col>
                            {disposalSites && nonWorkingPeriod && (
                              <DropDownTreeComponent
                                {...register("disposalSites")}
                                ref={(d: DropDownTreeComponent) => {
                                  setDropDownTreeComponent(d);
                                }}
                                change={dropDownTreeComponentOnChange}
                                treeSettings={disposalSiteTreeSettings}
                                fields={disposalSiteFields}
                                allowMultiSelection
                                showCheckBox
                                changeOnBlur={false}
                                mode="Box"
                                id="disposalSitesDropDown"
                                value={nonWorkingPeriod?.disposalSites}
                                htmlAttributes={{
                                  "data-testid": "ddlDisposalSites",
                                  "aria-required": "true"
                                }}
                              />
                            )}
                            <NoData
                              visible={
                                !apiErrors
                                && !getDisposalSitesIsSending
                                && !disposalSites
                              }
                              description="No Disposal Sites Found"
                            />
                          </Col>
                          <ErrorMessages
                            fieldName="disposalSites"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>

                      <Row className="mb-2 mt-3">
                        <Form.Group
                          as={Row}
                          controlId="active"
                          className="d-flex align-items-center"
                        >
                          <Form.Label
                            id="activeLabel"
                            className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                          >
                            Active:
                          </Form.Label>
                          <Form.Check
                            {...register("active")}
                            type="switch"
                            className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                            aria-required="true"
                            aria-labelledby="activeLabel"
                            defaultChecked={nonWorkingPeriod?.active}
                          />
                          <ErrorMessages
                            fieldName="active"
                            errors={errors}
                            validationFailures={null}
                          />
                        </Form.Group>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Container>
            </section>
          </Card.Body>
        </Form>
      </Card>
      <DeleteConfirmation
        title="Delete Non Working Period"
        onDelete={() => {
          handleDeleteNonWorkingPeriod();
        }}
        onHide={() => {
          setDeleteButtonPressed(false);
        }}
        isDeleting={deleteNonWorkingPeriodIsSending}
        show={deleteButtonPressed}
      >
        <p>Are you sure you want to delete this Non Working Period?</p>
      </DeleteConfirmation>
    </Page>
  );
};

export default UpdateNonWorkingPeriod;
